import { createContext } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Theme, ThemeProvider } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useFetchOrganizationsByIdQuery, useFetchOrganizationWebsiteByIdQuery } from "@/features/organizations/api/organizationsApi";
import MetaTags from "@/components/MetaTags";
import { useFetchContentQuery } from "@/features/organizations/api/contentApi";
import PageLoading from "@/components/page-loading/PageLoading";
import { useAppSelector } from "@/hooks";
import useOrganizationTheme from "@/features/organizations/useOrganizationTheme";
import { BaseElement, Organization, Template } from "@/types";
import { useFetchElementsQuery, useFetchTemplateByIdQuery } from "@/features/templates/templatesApi";

export interface IWebiteProvider {
  organization?: Organization | null;
  website?: Template | null;
  template?: Template | null;
  elements?: BaseElement[] | null;
  alias?: Template | string | null | undefined;
  theme?: Theme;
}

export const WebsiteContext = createContext<IWebiteProvider>({
  organization: undefined,
  website: undefined,
  template: undefined,
  elements: undefined,
  alias: undefined,
  theme: undefined,
});

type Params = {
  websiteId?: string;
  organizationId?: string;
  website?: string;
  organizationAlias?: string;
};

const WebsiteProvider = () => {
  const { websiteId, organizationAlias } = useParams<Params>();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);

  const organizationQuery = useFetchOrganizationsByIdQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });

  const organizationTheme = useOrganizationTheme(organizationQuery?.data as Organization);

  const websiteQuery = useFetchOrganizationWebsiteByIdQuery(
    {
      organizationId: organizationQuery?.data?.id || skipToken,
      websiteId: organizationQuery?.data?.website || skipToken,
    },
    {
      skip: !organizationQuery?.data?.id || !organizationQuery?.data?.website,
    }
  );

  const templateQuery = useFetchTemplateByIdQuery(websiteQuery?.data?.template || skipToken, {
    skip: Boolean(websiteId || !websiteQuery?.data?.template),
  });

  const contentQuery = useFetchContentQuery(organizationId || skipToken, {
    skip: !organizationId,
  });

  const webElementsQuery = useFetchElementsQuery(templateQuery?.data?.id || skipToken, {
    skip: !templateQuery?.data?.id,
  });


  if (organizationQuery.isLoading || websiteQuery.isLoading || templateQuery.isLoading || contentQuery.isLoading || webElementsQuery.isLoading) {
    return <PageLoading />;
  }

  if (!organizationQuery?.data || (!websiteQuery?.data && !templateQuery?.data?.data)) {
    return null;
  }

  return (
    <WebsiteContext.Provider
      value={{
        organization: organizationQuery?.data,
        website: websiteQuery?.data ?? templateQuery?.data,
        template: templateQuery?.data,
        elements: webElementsQuery?.data,
        alias: organizationAlias,
        theme: organizationTheme,
      }}
    >
      <MetaTags name={organizationQuery?.data?.title} title={organizationQuery?.data?.title} description={organizationQuery?.data?.slogan} image={organizationQuery?.data?.logo} />
      <ThemeProvider theme={organizationTheme}>
        <Outlet
          context={{ // TODO: replace with context
            website: websiteQuery?.data ?? templateQuery?.data,
            organization: organizationQuery?.data,
          }}
        />
      </ThemeProvider>
    </WebsiteContext.Provider>
  );
};

export default WebsiteProvider;
